/* ============================================================
   SU CART DRAWER — engine
   Spice Up Foods · spiceupfood.in
   File: assets/su-cart-drawer.js

   Gift rule: customer earns FREE SHIPPING at its threshold,
   plus ONE free gift — the highest gift tier unlocked.
   Lower gift tiers are superseded, never stacked.
   ============================================================ */
(function () {
  'use strict';

  var JSON_HEADERS = { 'Accept': 'application/json', 'Content-Type': 'application/json' };

  function SUCartDrawer(root) {
    this.root = root;
    this.config = this.readConfig();

    this.panel = root.querySelector('.su-cart__panel');
    this.itemsEl = root.querySelector('[data-su-items]');
    this.bodyEl = root.querySelector('.su-cart__body');
    this.emptyEl = root.querySelector('[data-su-empty]');
    this.countEl = root.querySelector('[data-su-count]');
    this.fillEl = root.querySelector('[data-su-fill]');
    this.amountsEl = root.querySelector('[data-su-amounts]');
    this.dotsEl = root.querySelector('[data-su-dots]');
    this.namesEl = root.querySelector('[data-su-names]');
    this.msgEl = root.querySelector('[data-su-msg]');
    this.subtotalEl = root.querySelector('[data-su-subtotal]');
    this.savedEl = root.querySelector('[data-su-saved]');
    this.discountsEl = root.querySelector('[data-su-discounts]');
    this.badgeEl = root.querySelector('[data-su-save-badge]');
    this.checkoutWrap = root.querySelector('[data-su-checkout-wrap]');
    this.checkoutBtn = root.querySelector('[data-su-checkout]');
    this.toastEl = root.querySelector('[data-su-toast]');
    this.burstEl = root.querySelector('[data-su-burst]');
    this.noteEl = root.querySelector('[data-su-note]');

    this.barEl = root.querySelector('[data-su-bar]');
    this.barFill = root.querySelector('[data-su-bar-fill]');
    this.barCount = root.querySelector('[data-su-bar-count]');
    this.barTotal = root.querySelector('[data-su-bar-total]');
    this.barMsg = root.querySelector('[data-su-bar-msg]');
    this.noteToggle = root.querySelector('[data-su-note-toggle]');

    this.cart = null;
    this.swapToken = 0;
    this.swapRejected = {};
    this.productCache = {};
    this.selfRequests = 0;
    this.giftSyncing = false;
    this.prevRewardKey = null;
    this.noteTimer = 0;
    this.toastTimer = 0;
    this.savingsRequestId = 0;
    this.scrollY = 0;

    window.SUCartDrawerInstance = this;

    this.buildProgressScaffold();
    this.watchInjectedNodes();
    this.guardCheckout();
    this.bindDrawerEvents();
    this.bindGlobalEvents();
    this.refresh(false);
    /* checkout-resume flows rebuild the cart moments AFTER page load,
       replacing it beneath our first sync. One late pass re-reads the
       cart and rewrites the attributes; sync is idempotent, so it is
       free when nothing changed. */
    var boot = this;
    setTimeout(function () { boot.refresh(false); }, 4000);
  }

  /* ---------------- config ---------------- */

  SUCartDrawer.prototype.readConfig = function () {
    var el = this.root.querySelector('[data-su-config]');
    var fallback = { rewards: [], text: {}, moneyFormat: '₹{{amount}}', currency: 'INR' };
    if (!el) return fallback;
    try {
      var cfg = JSON.parse(el.textContent);
      cfg.rewards = (cfg.rewards || [])
        .filter(function (r) { return Number(r.threshold) > 0; })
        .sort(function (a, b) { return Number(a.threshold) - Number(b.threshold); })
        .map(function (r) {
          return {
            type: r.type === 'gift' ? 'gift' : 'shipping',
            threshold: Number(r.threshold),
            title: r.title || '',
            icon: r.icon || (r.type === 'gift' ? '🎁' : '🚚'),
            image: r.image || '',
            variantId: r.variantId ? Number(r.variantId) : 0,
            value: Number(r.value) || 0
          };
        });
      cfg.swap = cfg.swap || {};
      cfg.swap.enabled = cfg.swap.enabled !== false;
      cfg.swap.floorPct = Number(cfg.swap.floorPct) || 0;
      cfg.swap.header = cfg.swap.header || 'Upgrade to {pack}';
      cfg.swap.cta = cfg.swap.cta || 'Swap';
      var splitList = function (v) {
        return String(v || '').split(',').map(function (x) {
          return x.replace(/\s+/g, ' ').trim().toLowerCase();
        }).filter(Boolean);
      };
      cfg.swap.fromTitles = splitList(cfg.swap.fromTitles);
      cfg.swap.toTitles = splitList(cfg.swap.toTitles);
      return cfg;
    } catch (err) {
      console.error('SU Cart: invalid config JSON', err);
      return fallback;
    }
  };

  SUCartDrawer.prototype.t = function (key, vars) {
    var s = (this.config.text && this.config.text[key]) || '';
    if (vars) {
      Object.keys(vars).forEach(function (k) {
        s = s.split('{' + k + '}').join(vars[k]);
      });
    }
    return s;
  };

  /* ---------------- helpers ---------------- */

  SUCartDrawer.prototype.rootUrl = function () {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  };

  SUCartDrawer.prototype.escapeHtml = function (v) {
    return String(v == null ? '' : v)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#039;');
  };

  SUCartDrawer.prototype.money = function (cents) {
    var amount = Number(cents || 0);
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function') {
      return window.Shopify.formatMoney(amount, this.config.moneyFormat);
    }
    try {
      return new Intl.NumberFormat('en-IN', {
        style: 'currency', currency: this.config.currency || 'INR', maximumFractionDigits: 0
      }).format(amount / 100);
    } catch (err) {
      return '₹' + Math.round(amount / 100).toLocaleString('en-IN');
    }
  };

  SUCartDrawer.prototype.request = function (path, options) {
    var self = this;
    self.selfRequests++;
    return fetch(this.rootUrl() + path, options)
      .then(function (response) {
        return response.json().catch(function () { return null; }).then(function (data) {
          if (!response.ok) {
            var message = (data && (data.description || data.message)) || 'Cart request failed';
            throw new Error(message);
          }
          return data;
        });
      })
      .finally(function () {
        self.selfRequests--;
      });
  };

  /* ---------------- gift logic (single highest gift) ---------------- */

  SUCartDrawer.prototype.giftVariantIds = function () {
    return this.config.rewards
      .filter(function (r) { return r.type === 'gift' && r.variantId; })
      .map(function (r) { return r.variantId; });
  };

  SUCartDrawer.prototype.isUpsellLine = function (item) {
    return !!(item.properties && item.properties._su_src === this.UPSELL_TAG);
  };

  SUCartDrawer.prototype.SWAP_TAG = 'Sswap';

  SUCartDrawer.prototype.packCount = function (title) {
    var t = String(title || '');
    /* Prefer the number right after "of" or "pack" — e.g. "Pack of 5", "5 Pack",
       "Value Pack of 3 (500g)" -> 3, never the 500. */
    var m = t.match(/(?:pack\s*of|of)\s*(\d+)/i)   /* "pack of 5" / "of 5" */
         || t.match(/(\d+)\s*[-\s]*pack/i);        /* "5 pack" / "5-pack" */
    if (m) return Number(m[1]);
    /* fall back to the last standalone number, which is more likely the count
       than a leading size (grams/ml usually come first) */
    var all = t.match(/\d+/g);
    return all ? Number(all[all.length - 1]) : 0;
  };

  SUCartDrawer.prototype.normTitle = function (t) {
    return String(t || '').replace(/\s+/g, ' ').trim().toLowerCase();
  };

  SUCartDrawer.prototype.isSwapFromLine = function (item) {
    return this.config.swap.fromTitles.indexOf(this.normTitle(item.variant_title)) !== -1;
  };

  /* Fetch the product's variants (cached) and pick the best upgrade target:
     first title in toTitles that exists, is available, is bigger, and clears
     the saving floor vs buying singles of the small pack. Returns null if none. */
  SUCartDrawer.prototype.getSwapTarget = function (item) {
    var self = this;
    if (!self.config.swap.enabled) return Promise.resolve(null);
    if (!self.isSwapFromLine(item)) return Promise.resolve(null);
    if (self.isGiftLine(item)) return Promise.resolve(null);
    if (!item.handle) return Promise.resolve(null);

    var smallUnitPrice = Number(item.final_price || item.price || 0); /* per-unit, in cents */
    if (!smallUnitPrice) return Promise.resolve(null);

    return self.getProduct(item.handle).then(function (product) {
      if (!product || !product.variants) return null;
      var byTitle = {};
      product.variants.forEach(function (v) {
        byTitle[self.normTitle(v.title)] = v;
      });

      for (var i = 0; i < self.config.swap.toTitles.length; i++) {
        var v = byTitle[self.config.swap.toTitles[i]];
        if (!v || v.available === false) continue;

        var targetQty = self.packCount(v.title);
        var smallQty = self.packCount(item.variant_title) || 1;
        if (!targetQty || targetQty <= smallQty) continue;

        var multiple = targetQty / smallQty;               /* e.g. 5 packs of 1 = 5 */
        var singlesCents = smallUnitPrice * multiple;
        var packCents = Number(v.price || 0);
        var save = singlesCents - packCents;
        if (save <= 0) continue;

        var pct = Math.round(save / singlesCents * 100);
        if (pct < self.config.swap.floorPct) continue;

        var perNow = smallUnitPrice;
        var perNew = Math.round(packCents / multiple);
        return {
          variantId: Number(v.id),
          variantTitle: v.title,
          packCents: packCents,
          save: save,
          pct: pct,
          perNow: perNow,
          perNew: perNew,
          perSave: perNow - perNew,
          multiple: multiple
        };
      }
      return null;
    }).catch(function () { return null; });
  };

  SUCartDrawer.prototype.isGiftLine = function (item) {
    if (this.giftVariantIds().indexOf(Number(item.variant_id)) !== -1) return true;
    return !!(item.properties && item.properties._su_gift);
  };

  /* subtotal of paid items only (gift lines excluded) in cents */
  SUCartDrawer.prototype.paidSubtotal = function (cart) {
    var self = this, total = 0;
    (cart.items || []).forEach(function (item) {
      if (!self.isGiftLine(item)) total += Number(item.final_line_price || 0);
    });
    return total;
  };

  SUCartDrawer.prototype.shippingReward = function () {
    var list = this.config.rewards.filter(function (r) { return r.type === 'shipping'; });
    return list.length ? list[0] : null;
  };

  SUCartDrawer.prototype.giftRewards = function () {
    return this.config.rewards.filter(function (r) { return r.type === 'gift'; });
  };

  /* the ONE gift earned: highest gift tier whose threshold is met */
  SUCartDrawer.prototype.earnedGift = function (subtotalCents) {
    var rupees = subtotalCents / 100, earned = null;
    this.giftRewards().forEach(function (g) {
      if (rupees >= g.threshold) earned = g;
    });
    return earned;
  };

  /* the gift to auto-add: highest EARNED gift that has a variant id */
  SUCartDrawer.prototype.desiredGiftVariant = function (subtotalCents) {
    var rupees = subtotalCents / 100, id = 0;
    this.giftRewards().forEach(function (g) {
      if (rupees >= g.threshold && g.variantId) id = g.variantId;
    });
    return id;
  };

  /* Keep the cart holding exactly ONE correct gift line (or none).
     Works on line keys, so it also cleans up duplicate gift lines created
     by any other app. Idempotent. */
  SUCartDrawer.prototype.syncGifts = function (cart) {
    var self = this;
    if (self.giftSyncing) return Promise.resolve(cart);

    var desired = self.desiredGiftVariant(self.paidSubtotal(cart));

    /* Police by the gift birthmark too, not only configured variants.
       A ₹0 line marked _su_gift whose tier is hidden or unearned is an
       orphan from an old cart, and it gets removed here. */
    var giftLines = (cart.items || []).filter(function (item) {
      return self.isGiftLine(item);
    });

    if (!giftLines.length && !desired) return Promise.resolve(cart);

    var keepKey = null;
    var removeKeys = [];

    giftLines.forEach(function (item) {
      if (desired && Number(item.variant_id) === desired && keepKey === null) {
        keepKey = item.key;                 /* first correct line survives */
        if (item.quantity !== 1) removeKeys.push({ key: item.key, qty: 1 });
      } else {
        removeKeys.push({ key: item.key, qty: 0 });   /* wrong tier or duplicate */
      }
    });

    var needsAdd = !!desired && keepKey === null;
    if (!removeKeys.length && !needsAdd) return Promise.resolve(cart);

    self.giftSyncing = true;

    /* changes must run one after another — each one re-keys the cart */
    var chain = Promise.resolve(cart);
    removeKeys.forEach(function (op) {
      chain = chain.then(function () {
        return self.request('cart/change.js', {
          method: 'POST', headers: JSON_HEADERS,
          body: JSON.stringify({ id: op.key, quantity: op.qty })
        });
      });
    });

    if (needsAdd) {
      chain = chain
        .then(function () {
          return self.request('cart/add.js', {
            method: 'POST', headers: JSON_HEADERS,
            body: JSON.stringify({ id: desired, quantity: 1, properties: { _su_gift: 'true' } })
          }).catch(function (err) {
            console.warn('SU Cart: gift add failed', err);
            self.toast('Gift could not be added: ' + (err.message || 'unknown error'));
          });
        })
        .then(function () {
          return self.request('cart.js', { method: 'GET', headers: { 'Accept': 'application/json' } });
        });
    }

    return chain
      .then(function (latest) { return latest && latest.items ? latest : cart; })
      .catch(function (err) { console.error('SU Cart: gift sync error', err); return cart; })
      .finally(function () { self.giftSyncing = false; });
  };

  /* ---------------- data flow ---------------- */

  SUCartDrawer.prototype.fetchCart = function () {
    return this.request('cart.js', { method: 'GET', headers: { 'Accept': 'application/json' } });
  };

  SUCartDrawer.prototype.refresh = function (openAfter) {
    var self = this;
    return self.fetchCart()
      .then(function (cart) { return self.syncGifts(cart); })
      .then(function (cart) {
        self.cart = cart;
        self.render(cart);
        if (openAfter) self.open();
      })
      .catch(function (err) { console.error('SU Cart: refresh error', err); });
  };

  /* Called by the global fetch patch when other scripts mutate the cart */
  SUCartDrawer.prototype.onExternalMutation = function (wasAdd) {
    var self = this;
    clearTimeout(self.externalTimer);
    self.externalTimer = setTimeout(function () {
      if (wasAdd) { self.afterAdd('Added to cart'); } else { self.refresh(false); }
    }, 60);
  };

  /* ---------------- rendering ---------------- */

  SUCartDrawer.prototype.buildProgressScaffold = function () {
    var self = this, rewards = self.config.rewards, n = rewards.length;
    if (!n || !self.dotsEl) return;
    /* one visible tier: the milestone ladder becomes a slim shipping
       bar. Un-hiding gift blocks restores the full rail automatically. */
    self.root.classList.toggle('su-single-tier', n === 1);
    var cols = 'repeat(' + n + ',1fr)';
    if (self.amountsEl) self.amountsEl.style.gridTemplateColumns = cols;
    if (self.namesEl) self.namesEl.style.gridTemplateColumns = cols;

    var amounts = '', dots = '', names = '';
    rewards.forEach(function (r, i) {
      var center = ((i + 0.5) / n * 100).toFixed(3);
      amounts += '<b>' + self.money(r.threshold * 100) + '</b>';
      var face = r.image
        ? '<img src="' + self.escapeHtml(r.image) + '" alt="" loading="lazy">'
        : self.escapeHtml(r.icon);
      var typeClass = r.type === 'shipping' ? ' is-shipping' : ' is-gifttier';
      dots += '<div class="su-cart__dot' + (r.image ? ' has-img' : '') + typeClass + '" data-su-dot="' + i + '" style="left:' + center + '%">' + face + '</div>';
      names += '<span data-su-name="' + i + '">' + self.escapeHtml(r.title) + '</span>';
    });
    if (self.amountsEl) self.amountsEl.innerHTML = amounts;
    self.dotsEl.innerHTML = dots;
    if (self.namesEl) self.namesEl.innerHTML = names;
  };

  SUCartDrawer.prototype.fillPercent = function (subtotalCents) {
    var rewards = this.config.rewards, n = rewards.length;
    if (!n) return 0;
    var rupees = subtotalCents / 100;
    if (n === 1) return Math.min(100, rupees / rewards[0].threshold * 100);
    var points = [[0, 0]];
    rewards.forEach(function (r, i) { points.push([r.threshold, (i + 0.5) / n * 100]); });
    if (rupees >= rewards[n - 1].threshold) return 100;
    for (var i = 1; i < points.length; i++) {
      if (rupees < points[i][0]) {
        var a = points[i - 1], b = points[i];
        return a[1] + (rupees - a[0]) / (b[0] - a[0]) * (b[1] - a[1]);
      }
    }
    return 100;
  };

  SUCartDrawer.prototype.render = function (cart) {
    if (!cart) return;
    var self = this;
    self.syncUpsellAttributes(cart);
    var paidCents = self.paidSubtotal(cart);
    var rupees = paidCents / 100;
    var ship = self.shippingReward();
    var shipEarned = !!(ship && rupees >= ship.threshold);
    var gift = self.earnedGift(paidCents);

    /* count text — paid items only */
    var count = 0;
    (cart.items || []).forEach(function (item) { if (!self.isGiftLine(item)) count += item.quantity; });
    if (self.countEl) {
      self.countEl.textContent = count === 0
        ? self.t('empty_subtitle')
        : count + ' ' + (count === 1 ? self.t('item_text') : self.t('items_text'));
    }
    self.updateHeaderBubble(count);

    /* progress */
    var fillPct = self.fillPercent(paidCents);
    if (self.fillEl) self.fillEl.style.width = fillPct + '%';
    /* single-tier mode: the tier's icon rides the fill tip */
    if (self.config.rewards.length === 1) {
      var rider = self.root.querySelector('[data-su-dot="0"]');
      if (rider) rider.style.left = Math.max(3, Math.min(97, fillPct)) + '%';
    }
    self.config.rewards.forEach(function (r, i) {
      var dot = self.root.querySelector('[data-su-dot="' + i + '"]');
      var name = self.root.querySelector('[data-su-name="' + i + '"]');
      var earned = rupees >= r.threshold;
      var isActiveGift = !!(gift && r === gift);
      if (dot) {
        dot.classList.toggle('is-gift', isActiveGift);
        dot.classList.toggle('is-done', earned && !isActiveGift);
        dot.classList.toggle('is-earned', earned);
        if (!r.image) dot.textContent = r.icon;
      }
      if (name) {
        name.classList.toggle('is-gift', isActiveGift);
        name.classList.toggle('is-done', earned && !isActiveGift);
      }
    });

    /* message */
    var nextGift = null;
    self.giftRewards().forEach(function (g) {
      if (rupees < g.threshold && !nextGift) nextGift = g;
    });
    var msg = '';
    if (count === 0) {
      msg = self.escapeHtml(self.t('msg_empty'));
    } else if (ship && !shipEarned) {
      msg = self.t('msg_next', {
        amount: '<b>' + self.money(ship.threshold * 100 - paidCents) + '</b>',
        reward: '<b>' + self.escapeHtml(ship.title) + ' ' + ship.icon + '</b>'
      });
    } else if (nextGift && !gift) {
      msg = self.t('msg_first_gift', {
        amount: '<b>' + self.money(nextGift.threshold * 100 - paidCents) + '</b>',
        reward: '<b>' + self.escapeHtml(nextGift.title) + ' ' + nextGift.icon + '</b>'
      });
    } else if (nextGift && gift) {
      msg = self.t('msg_upgrade', {
        amount: '<b>' + self.money(nextGift.threshold * 100 - paidCents) + '</b>',
        reward: '<b>' + self.escapeHtml(nextGift.title) + ' ' + nextGift.icon + '</b>'
      });
    } else {
      msg = self.escapeHtml(self.t('msg_max'));
    }
    if (count > 0 && (shipEarned || gift)) {
      var got = gift ? (gift.title + ' ' + gift.icon) : (ship.title + ' ' + ship.icon);
      msg += '<span class="su-cart__win">' + self.escapeHtml(self.t('msg_won', { rewards: got })) + '</span>';
    }
    if (self.msgEl) self.msgEl.innerHTML = msg;

    /* items */
    var empty = count === 0;
    if (self.emptyEl) self.emptyEl.hidden = !empty;
    var html = '';
    if (!empty) {
      /* tub row pinned FIRST, whatever the cart's insertion order:
         the record stays visible the moment the drawer opens */
      var tubRow = '', prods = '';
      (cart.items || []).forEach(function (item) {
        if ((item.handle || '') === self.TUB_HANDLE) { tubRow = self.tubRowHtml(item); return; }
        if (!self.isGiftLine(item)) prods += self.itemHtml(item);
      });
      html += tubRow + prods;
      (cart.items || []).forEach(function (item) {
        if (self.isGiftLine(item)) html += self.giftHtml(item, gift);
      });
    }
    if (self.itemsEl) self.itemsEl.innerHTML = html;

    /* swap upsell — one card under the first eligible small-pack line */
    self.renderSwap(cart);

    /* \u20B920 tub strip — rides the bottom of the scroll (placement B) */
    self.renderTubStrip(cart);

    /* hide upsells already in cart */
    self.root.querySelectorAll('[data-su-upsell-card]').forEach(function (card) {
      var vid = Number(card.getAttribute('data-variant-id') || 0);
      var inCart = (cart.items || []).some(function (i) { return Number(i.variant_id) === vid; });
      card.hidden = inCart;
    });

    /* discounts */
    self.renderDiscounts(cart);

    /* subtotal + note */
    if (self.subtotalEl) self.subtotalEl.textContent = self.money(cart.total_price || 0);
    if (self.noteEl && document.activeElement !== self.noteEl) {
      self.noteEl.value = cart.note || '';
    }
    if (self.checkoutBtn) self.checkoutBtn.disabled = empty;

    /* savings (async) */
    self.updateSavings(cart, gift);

    /* sticky bar */
    self.renderBar(paidCents, count, ship, shipEarned, gift, nextGift);

    /* celebrate reward changes */
    var key = (shipEarned ? 'S' : '') + (gift ? gift.threshold : '');
    if (self.prevRewardKey !== null && key !== self.prevRewardKey && (shipEarned || gift)) {
      self.burst();
      if (self.msgEl) {
        self.msgEl.classList.remove('pulse');
        void self.msgEl.offsetWidth;
        self.msgEl.classList.add('pulse');
      }
    }
    self.prevRewardKey = key;
  };

  SUCartDrawer.prototype.renderBar = function (paidCents, count, ship, shipEarned, gift, nextGift) {
    var self = this;
    if (!self.barEl) return;

    if (count === 0) {
      self.barEl.hidden = true;
      document.body.classList.remove('su-bar-on');
      return;
    }

    self.barEl.hidden = false;
    document.body.classList.add('su-bar-on');

    if (self.barCount) self.barCount.textContent = count + (count === 1 ? ' item' : ' items');
    if (self.barTotal) self.barTotal.textContent = self.money(paidCents);
    if (self.barFill) self.barFill.style.width = self.fillPercent(paidCents) + '%';

    if (self.barMsg) {
      var msg;
      if (ship && !shipEarned) {
        msg = self.money(ship.threshold * 100 - paidCents) + ' more → ' + ship.title + ' ' + ship.icon;
      } else if (nextGift) {
        msg = self.money(nextGift.threshold * 100 - paidCents) + ' more → ' + nextGift.title + ' ' + nextGift.icon;
      } else if (gift) {
        msg = gift.title + ' ' + gift.icon + ' unlocked';
      } else {
        msg = shipEarned && ship ? ship.title + ' ' + ship.icon + ' unlocked' : '';
      }
      self.barMsg.textContent = msg;
    }
  };

  SUCartDrawer.prototype.pulseBar = function () {
    var el = this.barEl;
    if (!el || el.hidden) return;
    el.classList.remove('is-pulsing');
    void el.offsetWidth;
    el.classList.add('is-pulsing');
  };

  /* First add of a session opens the drawer (they need to meet the reward ladder).
     After that, adds are quiet: toast + the bar updates and pulses. */
  SUCartDrawer.prototype.seenDrawer = function () {
    try { return sessionStorage.getItem('su_cart_seen') === '1'; } catch (e) { return false; }
  };
  SUCartDrawer.prototype.markSeen = function () {
    try { sessionStorage.setItem('su_cart_seen', '1'); } catch (e) {}
  };

  SUCartDrawer.prototype.afterAdd = function (label) {
    var self = this;
    var barLive = !!(self.barEl && self.config.bar && self.config.bar.enabled);
    var openIt = !barLive || !self.config.bar.openFirstAdd || !self.seenDrawer();

    return self.refresh(openIt).then(function () {
      if (openIt) {
        self.markSeen();
      } else {
        self.pulseBar();
        self.toast(label || 'Added to cart');
      }
    });
  };

  SUCartDrawer.prototype.renderSwap = function (cart) {
    var self = this;
    if (!self.config.swap.enabled || !self.itemsEl) return;

    /* clear any existing card synchronously so it never lingers on a wrong line */
    var existing = self.itemsEl.querySelector('.su-cart__swap');
    if (existing) existing.remove();

    /* rejected = product handles the shopper dismissed OR undid this session.
       Keyed by handle (line keys change on every swap/undo, handles don't). */
    if (!self.swapRejected) self.swapRejected = {};

    var eligible = (cart.items || []).filter(function (item) {
      return self.isSwapFromLine(item) && !self.isGiftLine(item);
    });
    if (!eligible.length) return;

    /* Order: products NOT yet rejected first (in cart order), rejected ones last.
       So after an undo, the next fresh product gets the offer before we ever
       circle back to something the shopper already backed out of. */
    var fresh = [], rejected = [];
    eligible.forEach(function (item) {
      if (self.swapRejected[item.handle]) rejected.push(item);
      else fresh.push(item);
    });
    var candidates = fresh.concat(rejected);

    /* resolve targets in order; render under the FIRST that qualifies */
    var token = ++self.swapToken;
    var idx = 0;
    var tryNext = function () {
      if (idx >= candidates.length) return;
      var item = candidates[idx++];
      self.getSwapTarget(item).then(function (target) {
        if (token !== self.swapToken) return;         /* a newer render superseded us */
        if (!target) { tryNext(); return; }
        self.injectSwapCard(item, target);
      });
    };
    tryNext();
  };

  SUCartDrawer.prototype.injectSwapCard = function (item, target) {
    var self = this;
    var line = self.itemsEl.querySelector('.su-cart__item[data-line-key="' + (window.CSS && CSS.escape ? CSS.escape(item.key) : item.key) + '"]');
    if (!line) return;
    if (line.parentNode.querySelector('.su-cart__swap')) return;

    var header = self.config.swap.header.split('{pack}').join(self.escapeHtml(target.variantTitle));
    var arrow = '<svg viewBox="0 0 24 24" width="13" height="13" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M7 5v14M7 5L4 8M7 5l3 3M17 19V5M17 19l-3-3M17 19l3-3"/></svg>';

    var card = document.createElement('div');
    card.className = 'su-cart__swap';
    card.innerHTML =
      '<div class="su-cart__swap-head">' +
        '<span class="su-cart__swap-title">' + arrow + header + '</span>' +
        '<span class="su-cart__swap-save">SAVE ' + self.money(target.perSave) + '/PACK</span>' +
      '</div>' +
      '<div class="su-cart__swap-body">' +
        '<div>' +
          '<div class="su-cart__swap-per"><span class="su-cart__swap-pernew">' + self.money(target.perNew) + '</span>' +
            '<span class="su-cart__swap-perunit">/pack</span>' +
            '<span class="su-cart__swap-perold">' + self.money(target.perNow) + '</span></div>' +
          '<div class="su-cart__swap-cap">Save <b>' + self.money(target.save) + '</b> total on ' + target.multiple + ' packs</div>' +
        '</div>' +
        '<button type="button" class="su-cart__swap-btn" data-su-swap>' + arrow + self.escapeHtml(self.config.swap.cta) + '</button>' +
      '</div>' +
      '<button type="button" class="su-cart__swap-x" data-su-swap-dismiss aria-label="Dismiss">×</button>';

    card.setAttribute('data-line-key', item.key);
    card.setAttribute('data-handle', item.handle || '');
    card.setAttribute('data-variant-id', target.variantId);
    card.setAttribute('data-save', target.save);
    card.setAttribute('data-pack-price', target.packCents);
    line.parentNode.insertBefore(card, line.nextSibling);
  };

  /* Execute the swap: remove the small-pack line, add the target variant,
     tag the order (su_swap count + su_swap_value). Reward ladder re-syncs
     automatically because refresh() re-runs gift logic afterwards. */
  SUCartDrawer.prototype.doSwap = function (lineKey, variantId, saveCents, packCents, button) {
    var self = this;
    self.setLoading(button, true);

    var line = (self.cart && self.cart.items || []).filter(function (i) { return i.key === lineKey; })[0];
    var fromQty = line ? Number(line.quantity || 1) : 1;   /* how many singles they had */
    var fromVariant = line ? Number(line.variant_id) : 0;
    var fromTitle = line ? (line.variant_title || '') : '';
    var srcTag = (line && line.properties && line.properties._su_src) || '';   /* keep attribution (Stest or Sfbt) across the swap */

    /* REVENUE LIFT = what the swap ADDED to the order:
       big pack price minus what their singles were worth. This is the number
       tracked in su_swap_value (how much we upsold, not what they saved). */
    var unitCents = line ? Number(line.final_price || line.price || 0) : 0;
    var liftCents = Math.max(0, Number(packCents || 0) - unitCents * fromQty);

    var props = {
      _su_swapped: 'true',
      _su_from_variant: String(fromVariant),
      _su_from_title: fromTitle,
      _su_from_qty: String(fromQty),
      _su_lift: String(Math.round(liftCents / 100))
    };
    if (srcTag) props._su_src = srcTag;                    /* the originating machine still gets credit */

    return self.request('cart/change.js', {
      method: 'POST', headers: JSON_HEADERS,
      body: JSON.stringify({ id: lineKey, quantity: 0 })   /* discard ALL the singles */
    })
      .then(function () {
        return self.request('cart/add.js', {
          method: 'POST', headers: JSON_HEADERS,
          body: JSON.stringify({ id: Number(variantId), quantity: 1, properties: props })
        });
      })

      .then(function () { return self.refresh(false); })
      .then(function () { self.toast('Upgraded — you saved ' + self.money(saveCents)); })
      .catch(function (err) { self.toast(err.message || 'Could not upgrade'); return self.refresh(false); })
      .finally(function () { self.setLoading(button, false); });
  };

  /* Revert a swapped line back to the original small pack. Records the undo
     and WHICH pack they backed out of, so you can see e.g. "most undos are
     from Pack of 5" and test Pack of 3 as the target next. */
  SUCartDrawer.prototype.undoSwap = function (lineKey, link) {
    var self = this;
    var line = (self.cart && self.cart.items || []).filter(function (i) { return i.key === lineKey; })[0];
    if (!line || !line.properties || !line.properties._su_from_variant) return;

    var fromVariant = Number(line.properties._su_from_variant);
    var fromTitleWas = line.variant_title || '';        /* the big pack they're leaving */
    var restoreQty = Number(line.properties._su_from_qty || line.quantity || 1);  /* how many singles they had before */
    var handle = line.handle || '';

    /* An undo is a rejection: deprioritize this product so the swap card goes to
       a fresh item next, not straight back to the one they just backed out of. */
    if (handle) { if (!self.swapRejected) self.swapRejected = {}; self.swapRejected[handle] = true; }

    if (link) link.style.pointerEvents = 'none';

    return self.request('cart/change.js', {
      method: 'POST', headers: JSON_HEADERS,
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    })
      .then(function () {
        var backProps = null;
        if (line.properties && line.properties._su_src) backProps = { _su_src: line.properties._su_src };
        return self.request('cart/add.js', {
          method: 'POST', headers: JSON_HEADERS,
          body: JSON.stringify(backProps
            ? { id: fromVariant, quantity: restoreQty, properties: backProps }   /* keep upsell attribution */
            : { id: fromVariant, quantity: restoreQty })
        });
      })
      .then(function () { return self.bumpUndoTracking(fromTitleWas); })
      .then(function () {
        /* let this line offer the swap again if they change their mind */
        return self.refresh(false);
      })
      .then(function () { self.toast('Reverted to ' + (line.properties._su_from_title || 'smaller pack')); })
      .catch(function (err) { self.toast(err.message || 'Could not revert'); return self.refresh(false); });
  };

  /* Single source of truth for the whole su_ ledger: everything is recomputed
     from the LIVE cart lines on every render. su_swap = surviving upgraded
     lines, su_swap_value = their total revenue lift, su_swap_items = which
     products moved to which packs. Only su_swap_undo and su_swap_undo_from
     are event logs (written once per undo). Nothing else increments counters,
     so what the order shows is always exactly what was in the cart at pay. */
  /* Compress an undo-from list: "Pack of 5 x3, Pack of 3 x1" instead of a
     run-on repeat. Merges case-insensitively; accepts the old comma format. */
  SUCartDrawer.prototype.addToFromList = function (existing, title) {
    var map = {}, order = [];
    String(existing || '').split(',').forEach(function (part) {
      part = part.trim();
      if (!part) return;
      var m = part.match(/^(.*?)\s*x(\d+)$/i);
      var label = (m ? m[1] : part).trim();
      var count = m ? Number(m[2]) : 1;
      var k = label.toLowerCase();
      if (!map[k]) { map[k] = { label: label, count: 0 }; order.push(k); }
      map[k].count += count;
    });
    var k2 = String(title || '').trim().toLowerCase();
    if (k2) {
      if (!map[k2]) { map[k2] = { label: String(title).trim(), count: 0 }; order.push(k2); }
      map[k2].count += 1;
    }
    return order.map(function (k) {
      var e = map[k];
      return e.count > 1 ? (e.label + ' x' + e.count) : e.label;
    }).join(', ');
  };

  /* One human line that tells the whole story at a glance in admin. */
  SUCartDrawer.prototype.suSummary = function (a) {
    var bits = [];
    var swapVal = Number(a.su_swap_value || 0), swaps = Number(a.su_swap || 0), undos = Number(a.su_swap_undo || 0);
    var upVal = Number(a.su_upsell_value || 0), upItems = Number(a.su_upsell_items || 0);
    if (swaps > 0 || swapVal > 0) {
      var names = a.su_swap_names || [];
      var who = names.length === 0 ? '' :
        names.length <= 2 ? ': ' + names.join(', ') :
        ': ' + names.length + ' packs upgraded';
      bits.push('Swap +\u20B9' + swapVal + who + (undos ? ' (' + undos + ' undo)' : ''));
    } else if (undos > 0) {
      bits.push('Swap undone x' + undos);
    }
    if (upItems > 0 || upVal > 0) {
      bits.push('Upsell \u20B9' + upVal + ' (' + upItems + (upItems === 1 ? ' item' : ' items') + ')');
    }
    var fbtVal = Number(a.su_fbt_value || 0), fbtItems = Number(a.su_fbt_items || 0);
    if (fbtItems > 0 || fbtVal > 0) {
      bits.push('FBT \u20B9' + fbtVal + ' (' + fbtItems + (fbtItems === 1 ? ' item' : ' items') + ')');
    }
    return bits.join(' | ');
  };

  /* su_swap_value = total REVENUE LIFT from swaps (₹ upsold), not customer savings */
  /* su_swap_undo = number of reverts; su_swap_undo_from = which packs they
     backed out of (accumulated list, e.g. "Pack of 5, Pack of 5, Pack of 3"). */
  SUCartDrawer.prototype.bumpUndoTracking = function (fromTitle) {
    var self = this;
    var cart = self.cart || {};
    var attrs = cart.attributes || {};
    var count = Number(attrs.su_swap_undo || 0) + 1;
    var fromList = self.addToFromList(attrs.su_swap_undo_from, fromTitle);
    /* count and value of surviving swaps are recomputed from live lines by
       syncUpsellAttributes on the next render, so this only logs the event */
    return self.request('cart/update.js', {
      method: 'POST', headers: JSON_HEADERS,
      body: JSON.stringify({ attributes: {
        su_swap_undo: String(count),
        su_swap_undo_from: fromList
      } })
    }).catch(function () {});
  };

  SUCartDrawer.prototype.itemHtml = function (item) {
    var self = this;
    var title = self.escapeHtml(item.product_title || item.title || '');
    var url = self.escapeHtml(item.url || '#');
    var img = item.image ? '<img src="' + self.escapeHtml(item.image) + '" alt="' + title + '" loading="lazy">' : '';
    var variant = (item.variant_title && item.variant_title !== 'Default Title')
      ? '<div class="su-cart__item-variant">' + self.escapeHtml(item.variant_title) + '</div>' : '';
    var priceNow = self.money(item.final_line_price || 0);
    var priceWas = '';
    if (item.original_line_price && item.original_line_price > item.final_line_price) {
      priceWas = '<s>' + self.money(item.original_line_price) + '</s>';   /* line-level discount */
    } else if (item.compare_at_price && item.compare_at_price * item.quantity > item.final_line_price) {
      priceWas = '<s>' + self.money(item.compare_at_price * item.quantity) + '</s>';  /* MRP already on the line */
    }
    /* if compare-at isn't on the line object, the async filler will add it via data attr */
    var wasSlot = priceWas || '<s class="su-cart__item-was" data-su-was hidden></s>';
    return '' +
      '<article class="su-cart__item" data-su-line data-line-key="' + self.escapeHtml(item.key) + '" data-handle="' + self.escapeHtml(item.handle || '') + '" data-variant-id="' + Number(item.variant_id || 0) + '" data-qty="' + Number(item.quantity || 1) + '" data-final="' + Number(item.final_line_price || 0) + '">' +
        '<a class="su-cart__item-img" href="' + url + '">' + img + '</a>' +
        '<div>' +
          '<h4><a href="' + url + '">' + title + '</a></h4>' + variant +
          '<div class="su-cart__item-price">' + priceNow + wasSlot + '</div>' +
          '<div class="su-cart__item-actions">' +
            '<div class="su-cart__qty">' +
              '<button type="button" data-su-minus aria-label="Decrease quantity">−</button>' +
              '<input type="number" min="0" value="' + Number(item.quantity || 1) + '" data-su-qty aria-label="Quantity">' +
              '<button type="button" data-su-plus aria-label="Increase quantity">+</button>' +
            '</div>' +
            '<div class="su-cart__item-links">' +
              (item.properties && item.properties._su_swapped
                ? '<button type="button" class="su-cart__undo" data-su-undo>Undo upgrade</button>'
                : '') +
              '<button type="button" class="su-cart__remove" data-su-remove>Remove</button>' +
            '</div>' +
          '</div>' +
        '</div>' +
      '</article>';
  };

  SUCartDrawer.prototype.giftHtml = function (item, activeGift) {
    var self = this;
    var title = self.escapeHtml(item.product_title || item.title || (activeGift && activeGift.title) || 'Free gift');
    var img = item.image ? '<img src="' + self.escapeHtml(item.image) + '" alt="' + title + '" loading="lazy">'
                         : (activeGift ? self.escapeHtml(activeGift.icon) : '🎁');
    var value = (activeGift && activeGift.value > 0) ? '<s>' + self.money(activeGift.value * 100) + '</s>' : '';
    return '' +
      '<article class="su-cart__item su-cart__item--gift">' +
        '<div class="su-cart__item-img">' + img + '</div>' +
        '<div>' +
          '<h4>' + title + ' <span class="su-cart__free-pill">FREE</span></h4>' +
          '<div class="su-cart__item-variant su-cart__item-giftnote">🎁 Free gift added</div>' +
          '<div class="su-cart__item-price">' + self.money(0) + value + '</div>' +
        '</div>' +
      '</article>';
  };

  SUCartDrawer.prototype.renderDiscounts = function (cart) {
    var self = this;
    if (!self.discountsEl) return;
    var apps = cart.cart_level_discount_applications || [];
    if (!apps.length) { self.discountsEl.hidden = true; self.discountsEl.innerHTML = ''; return; }
    self.discountsEl.hidden = false;
    self.discountsEl.innerHTML = apps.map(function (d) {
      return '<div class="su-cart__discount-pill"><span>' + self.escapeHtml(d.title || 'Discount') + '</span>' +
             '<strong>−' + self.money(d.total_allocated_amount || 0) + '</strong></div>';
    }).join('');
  };

  /* ---------------- savings ---------------- */

  SUCartDrawer.prototype.getProduct = function (handle) {
    var self = this;
    if (!handle) return Promise.resolve(null);
    if (self.productCache[handle]) return Promise.resolve(self.productCache[handle]);
    return fetch(self.rootUrl() + 'products/' + encodeURIComponent(handle) + '.js', {
      headers: { 'Accept': 'application/json' }
    }).then(function (r) { return r.ok ? r.json() : null; })
      .then(function (p) { if (p) self.productCache[handle] = p; return p; })
      .catch(function () { return null; });
  };

  SUCartDrawer.prototype.updateSavings = function (cart, activeGift) {
    var self = this;
    if (!self.badgeEl || !self.checkoutWrap) return;
    var requestId = ++self.savingsRequestId;

    var items = (cart.items || []).filter(function (i) { return !self.isGiftLine(i); });
    var lineTotal = 0;

    var perItem = items.map(function (item) {
      var qty = Number(item.quantity || 1);
      var finalLine = Number(item.final_line_price || 0);
      lineTotal += finalLine;
      var compareAt = Number(item.compare_at_price || 0);
      var base = Promise.resolve(compareAt);
      if (!compareAt && item.handle && item.variant_id) {
        base = self.getProduct(item.handle).then(function (product) {
          if (!product || !product.variants) return 0;
          var v = product.variants.find(function (x) { return Number(x.id) === Number(item.variant_id); });
          return v && v.compare_at_price ? Number(v.compare_at_price) : 0;
        });
      }
      return base.then(function (cmp) {
        /* fill the struck MRP on the line if it wasn't already rendered */
        if (cmp && cmp * qty > finalLine) {
          var lineEl = self.itemsEl && self.itemsEl.querySelector('.su-cart__item[data-line-key="' + (window.CSS && CSS.escape ? CSS.escape(item.key) : item.key) + '"]');
          if (lineEl) {
            var wasEl = lineEl.querySelector('[data-su-was]');
            if (wasEl) { wasEl.textContent = self.money(cmp * qty); wasEl.hidden = false; }
          }
        }
        if (cmp && cmp * qty > finalLine) return cmp * qty - finalLine;
        var orig = Number(item.original_line_price || 0);
        return orig > finalLine ? orig - finalLine : 0;
      });
    });

    Promise.all(perItem).then(function (parts) {
      if (requestId !== self.savingsRequestId) return;
      var saved = parts.reduce(function (a, b) { return a + b; }, 0);
      saved += Math.max(0, lineTotal - self.paidSubtotal(cart)); /* cart-level discounts on paid items */
      if (activeGift && activeGift.value > 0) saved += activeGift.value * 100;
      saved = Math.round(saved);

      if (saved > 0 && items.length) {
        if (self.savedEl) { self.savedEl.hidden = false; self.savedEl.textContent = 'You save ' + self.money(saved); }
      } else {
        if (self.savedEl) self.savedEl.hidden = true;
      }
      self.checkoutWrap.classList.remove('has-savings');
    });
  };

  /* ---------------- header count ---------------- */

  SUCartDrawer.prototype.updateHeaderBubble = function (count) {
    var self = this;
    self.lastCount = count;
    self.paintBubble(count);
    [120, 400, 900, 1600].forEach(function (ms) {
      setTimeout(function () { self.paintBubble(self.lastCount); }, ms);
    });
  };

  SUCartDrawer.prototype.paintBubble = function (count) {
    var text = String(count);

    /* 1. the usual suspects */
    document.querySelectorAll(
      '.cart-count-bubble span[aria-hidden="true"], [data-cart-count], .cart-count, ' +
      '.header-cart-count, .cart-icon-count, .cart__count, .site-header__cart-indicator'
    ).forEach(function (el) {
      if (el.closest('[data-su-cart]')) return;
      el.textContent = text;
    });

    /* 2. anything inside a header cart link that is just a number */
    document.querySelectorAll(
      'header a[href*="/cart"], .header a[href*="/cart"], #cart-icon-bubble, .cart-icon-bubble, .header__icon--cart'
    ).forEach(function (trigger) {
      if (trigger.closest('[data-su-cart]')) return;
      trigger.querySelectorAll('span, div, b, i, sup').forEach(function (node) {
        if (node.children.length) return;
        var t = (node.textContent || '').trim();
        if (/^\d+$/.test(t)) node.textContent = text;
      });
    });

    /* 3. hide empty bubbles */
    document.querySelectorAll('.cart-count-bubble').forEach(function (el) {
      if (el.closest('[data-su-cart]')) return;
      if (count > 0) el.removeAttribute('hidden'); else el.setAttribute('hidden', '');
    });
  };

  /* ---------------- cart operations ---------------- */

  /* Optimistic quantity change: the UI updates instantly, the server catches up.
     Rapid clicks collapse into a single request. */
  SUCartDrawer.prototype.changeLine = function (lineKey, quantity) {
    var self = this;
    if (!self.cart || !lineKey) return Promise.resolve();

    quantity = Math.max(0, Math.floor(Number(quantity) || 0));

    /* Resolve the line NOW and remember its stable identity (variant id + a
       signature of its properties). Shopify re-keys lines whenever the cart
       changes, so by the time this op runs the `key` may be dead — we re-find
       the line by identity at flush time instead of trusting the key. */
    var items = self.cart.items || [];
    var line = null, lineIndex = -1;
    for (var i = 0; i < items.length; i++) {
      if (items[i].key === lineKey) { line = items[i]; lineIndex = i; break; }
    }
    if (!line) return Promise.resolve();

    var identity = {
      key: lineKey,
      variantId: Number(line.variant_id || line.id || 0),
      propsSig: JSON.stringify(line.properties || {}),
      quantity: quantity
    };

    /* optimistic local update for instant feedback */
    if (quantity === 0) {
      self.cart.items = items.filter(function (it) { return it.key !== lineKey; });
    } else {
      line.quantity = quantity;
      line.final_line_price = Number(line.final_price || 0) * quantity;
      line.original_line_price = Number(line.original_price || line.final_price || 0) * quantity;
      line.line_price = line.final_line_price;
    }
    var total = 0;
    (self.cart.items || []).forEach(function (it) { total += Number(it.final_line_price || 0); });
    self.cart.total_price = total;
    self.render(self.cart);

    /* de-dupe: the latest intent for a given line supersedes earlier ones */
    self.pendingOps = self.pendingOps || [];
    self.pendingOps = self.pendingOps.filter(function (op) { return op.key !== lineKey; });
    self.pendingOps.push(identity);
    self.setSyncing(true);

    clearTimeout(self.flushTimer);
    self.flushTimer = setTimeout(function () { self.flushQty(); }, 220);
    return Promise.resolve();
  };

  /* Flush queued quantity changes ONE AT A TIME, each using cart/change.js by
     LINE NUMBER (1-based index) resolved against the freshest cart returned by
     the previous call. Using the line number, not the volatile key, is what
     kills the "invalid line parameter" errors and the item-comes-back bug. */
  SUCartDrawer.prototype.flushQty = function () {
    var self = this;
    clearTimeout(self.flushTimer);
    self.flushTimer = null;
    if (self.flushing) return;
    var ops = self.pendingOps || [];
    if (!ops.length) { self.setSyncing(false); return; }

    self.pendingOps = [];
    self.flushing = true;

    /* start from the server's current truth so line numbers are valid */
    var chain = self.request('cart.js', { method: 'GET', headers: JSON_HEADERS });

    ops.forEach(function (op) {
      chain = chain.then(function (cart) {
        var list = (cart && cart.items) || [];
        /* re-find this line in the CURRENT cart by identity, not stale key */
        var lineNo = 0;
        for (var i = 0; i < list.length; i++) {
          var it = list[i];
          if (Number(it.variant_id || it.id || 0) === op.variantId &&
              JSON.stringify(it.properties || {}) === op.propsSig) {
            lineNo = i + 1;   /* cart/change.js `line` is 1-based */
            break;
          }
        }
        if (!lineNo) return cart;   /* line already gone (e.g. removed) — skip safely */

        return self.request('cart/change.js', {
          method: 'POST', headers: JSON_HEADERS,
          body: JSON.stringify({ line: lineNo, quantity: op.quantity })
        });
      });
    });

    return chain
      .then(function (cart) { return self.syncGifts(cart); })
      .then(function (cart) { self.cart = cart; self.render(cart); })
      .catch(function (err) {
        console.error('SU Cart: quantity error', err);
        self.toast('Could not update cart');
        return self.refresh(false);        /* resync from server on any failure */
      })
      .finally(function () {
        self.flushing = false;
        if ((self.pendingOps || []).length) {
          self.flushQty();                 /* drain anything queued while we ran */
        } else {
          self.setSyncing(false);
        }
      });
  };

  SUCartDrawer.prototype.setSyncing = function (on) {
    if (!this.checkoutBtn) return;
    this.checkoutBtn.classList.toggle('is-syncing', !!on);
  };

  SUCartDrawer.prototype.isDirty = function () {
    return !!(this.flushing || (this.pendingOps || []).length || this.flushTimer);
  };

  /* Never let checkout fire on a cart Shopify hasn't confirmed yet. */
  SUCartDrawer.prototype.guardCheckout = function () {
    var self = this;
    var form = self.root.querySelector('.su-cart__checkout-form');
    if (!form) return;

    form.addEventListener('submit', function (event) {
      if (!self.isDirty()) return;                 /* clean cart — let it through */

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();

      clearTimeout(self.flushTimer);
      self.toast('Updating your cart…');

      var waitForClean = function (tries) {
        if (!self.isDirty()) {
          self.setSyncing(false);
          form.submit();                            /* real submit, guard already passed */
          return;
        }
        if (tries <= 0) { self.setSyncing(false); form.submit(); return; }
        setTimeout(function () { waitForClean(tries - 1); }, 120);
      };

      Promise.resolve(self.flushQty()).then(function () { waitForClean(40); });
    }, true);
  };

  /* Adds coming from the drawer's "You may also like" row.
     Tagged twice so the KPI is readable two ways:
       - line item property  _su_src = Stest   (which product came from the upsell)
       - cart attribute      su_upsell = Stest (which ORDERS used the upsell — shows
                             in Shopify admin under the order's Additional details) */
  SUCartDrawer.prototype.UPSELL_TAG = 'Stest';
  SUCartDrawer.prototype.FBT_TAG = 'Sfbt';
  SUCartDrawer.prototype.TUB_TAG = 'Stub';
  SUCartDrawer.prototype.TUB_HANDLE = 'empty-meal-tub';
  SUCartDrawer.prototype.TUB_POUCH_PREFIXES = ['PN', 'PJ', 'PK'];   /* pouch SKU families; tubs are TN/TJ/TK */

  SUCartDrawer.prototype.addVariant = function (variantId, button) {
    var self = this;
    self.setLoading(button, true);

    return self.request('cart/add.js', {
      method: 'POST', headers: JSON_HEADERS,
      body: JSON.stringify({
        id: Number(variantId),
        quantity: 1,
        properties: { _su_src: self.UPSELL_TAG }
      })
    })
      .then(function () { return self.afterAdd('Added to cart'); })
      .catch(function (err) { self.toast(err.message || 'Could not add product'); })
      .finally(function () { self.setLoading(button, false); });
  };

  /* Recompute upsell KPI from the LIVE cart and write it to cart attributes,
     so the number always reflects what actually gets ordered (removals count).
     Only writes when the value changed, to avoid needless requests. */
  SUCartDrawer.prototype.syncUpsellAttributes = function (cart) {
    var self = this;
    if (!cart || !cart.items) return;

    var value = 0, units = 0, fbtValue = 0, fbtUnits = 0, tubUnits = 0;
    var swapCount = 0, swapValue = 0, swapParts = [], swapNames = [];
    cart.items.forEach(function (item) {
      /* tubs are counted by the line's own identity, not its birth
         property: checkout-resume rebuilds (Spurr/Shiprocket abandoned
         cart links) recreate lines without our properties, and the
         handle is the one thing a tub line cannot lose */
      if ((item.handle || '') === self.TUB_HANDLE) {
        tubUnits += Number(item.quantity || 0);
        return;
      }
      var p = item.properties || {};
      var tag = p._su_src;
      if (tag === self.UPSELL_TAG) {
        value += Number(item.final_line_price || 0);
        units += Number(item.quantity || 0);
      } else if (tag === self.FBT_TAG) {
        fbtValue += Number(item.final_line_price || 0);
        fbtUnits += Number(item.quantity || 0);
      }
      if (p._su_swapped === 'true') {
        var lift = Number(p._su_lift || p._su_saved || 0);   /* _su_saved = legacy lines from the old build */
        swapCount += 1;
        swapValue += lift;
        var fromN = self.packCount(p._su_from_title || '');
        var toN = self.packCount(item.variant_title || '');
        var fq = Number(p._su_from_qty || 1);
        var nm = item.product_title || item.title || 'Item';
        var move = (fromN && toN)
          ? (fq > 1 ? fq + 'x' : '') + 'P' + fromN + '\u2192P' + toN
          : String(p._su_from_title || '?') + '\u2192' + String(item.variant_title || '?');
        swapParts.push(nm + ' ' + move + ' +\u20B9' + lift);
        swapNames.push(nm + ' ' + move);
      }
    });
    var wantSwapItems = swapParts.slice(0, 3).join('; ') +
      (swapParts.length > 3 ? '; +' + (swapParts.length - 3) + ' more' : '');
    if (wantSwapItems.length > 240) wantSwapItems = wantSwapItems.slice(0, 239) + '\u2026';

    var attrs = cart.attributes || {};
    var wantValue = units > 0 ? String(Math.round(value / 100)) : '';
    var wantFbt = fbtUnits > 0 ? String(Math.round(fbtValue / 100)) : '';
    var wantSwapV = swapCount > 0 ? String(swapValue) : '';
    /* tubs are a visible line item with their own SKU, so one glanceable
       attribute is enough: su_tub = quantity, empty when none */
    var wantTub = tubUnits > 0 ? String(tubUnits) : '';

    var next = {
      su_swap: swapCount > 0 ? String(swapCount) : '', su_swap_value: wantSwapV,
      su_swap_names: swapNames, su_swap_undo: attrs.su_swap_undo,
      su_upsell_items: units > 0 ? String(units) : '', su_upsell_value: wantValue,
      su_fbt_items: fbtUnits > 0 ? String(fbtUnits) : '', su_fbt_value: wantFbt,
      su_tub: wantTub
    };
    var wantSummary = self.suSummary(next);
    if (wantSummary.length > 240) wantSummary = wantSummary.slice(0, 239) + '\u2026';

    var same = String(attrs.su_upsell_value || '') === wantValue &&
               String(attrs.su_fbt_value || '') === wantFbt &&
               String(attrs.su_tub || '') === wantTub &&
               String(attrs.su_swap_value || '') === wantSwapV &&
               String(attrs.su_swap_items || '') === wantSwapItems &&
               String(attrs.su_summary || '') === wantSummary;
    if (same) return;   /* every story line already correct */

    var payload = {
      su_swap:          swapCount > 0 ? String(swapCount) : '',
      su_swap_value:    wantSwapV,
      su_swap_items:    wantSwapItems,
      su_upsell:        units > 0 ? self.UPSELL_TAG : '',
      su_upsell_items:  units > 0 ? String(units) : '',
      su_upsell_value:  wantValue,
      su_fbt:           fbtUnits > 0 ? self.FBT_TAG : '',
      su_fbt_items:     fbtUnits > 0 ? String(fbtUnits) : '',
      su_fbt_value:     wantFbt,
      su_tub:           wantTub,
      su_tub_items:     '',   /* legacy keys from the trio era: always */
      su_tub_value:     '',   /* cleared so old carts shed their fossils */
      su_summary:       wantSummary
    };

    self.request('cart/update.js', {
      method: 'POST', headers: JSON_HEADERS,
      body: JSON.stringify({ attributes: payload })
    }).catch(function () { /* KPI attribute is best-effort, never blocks the cart */ });
  };

  /* ---------------- \u20B920 empty tub strip (placement B) ----------------
     Shows only when the cart holds pouch-format lines (SKU families PN,
     PJ, PK). Tub meals ship in their own tub, so they never trigger it,
     and the empty tub itself never counts as a pouch. The tub product is
     found by HANDLE, so SKU renames in Uniware are harmless, and its
     price and variant id are read live from the product. While the
     listing is DRAFT the fetch fails and the strip simply never renders.
     Adds carry _su_src: Stub, recomputed into ONE order attribute,
     su_tub = quantity, by the same single-writer sync as every other
     machine. Money and items are readable from the tub's own cart line.
     Placement: pinned at the top of the drawer footer, above the
     subtotal and BUY NOW, so it is always visible without scrolling. */

  SUCartDrawer.prototype.loadTubInfo = function () {
    var self = this;
    if (self.tubInfoPromise) return self.tubInfoPromise;
    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    self.tubInfoPromise = fetch(root + 'products/' + self.TUB_HANDLE + '.js', { headers: { 'Accept': 'application/json' } })
      .then(function (r) { if (!r.ok) throw new Error('tub'); return r.json(); })
      .then(function (p) {
        var v = p && p.variants && p.variants[0];
        if (!v) throw new Error('tub');
        self.tubInfo = { variantId: v.id, price: Math.round(Number(v.price) / 100) };
        if (self.cart) self.renderTubStrip(self.cart);
        return self.tubInfo;
      })
      .catch(function () { self.tubInfo = null; return null; });
    return self.tubInfoPromise;
  };

  SUCartDrawer.prototype.tubPouchUnits = function (cart) {
    var self = this, total = 0;
    (cart.items || []).forEach(function (item) {
      if (self.isGiftLine(item)) return;
      if ((item.handle || '') === self.TUB_HANDLE) return;
      var sku = String(item.sku || '').toUpperCase();
      var isPouch = self.TUB_POUCH_PREFIXES.some(function (pre) { return sku.indexOf(pre) === 0; });
      if (!isPouch) return;
      total += (self.packCount(item.variant_title || '') || 1) * Number(item.quantity || 0);
    });
    return total;
  };

  SUCartDrawer.prototype.tubLine = function (cart) {
    var self = this, found = null;
    (cart.items || []).forEach(function (item) {
      if (!found && (item.handle || '') === self.TUB_HANDLE) found = item;
    });
    return found;
  };

  /* Compact list presence for the tubs: name, count, money, Remove.
     One slim row, no photo block, no second stepper (the strip holds
     the controls). Uses the standard line wiring so Remove just works. */
  SUCartDrawer.prototype.tubRowHtml = function (item) {
    var self = this;
    var q = Number(item.quantity || 0);
    var money = self.money(Number(item.final_line_price || 0));
    return '' +
      '<article class="su-cart__tubline" data-su-line data-line-key="' + self.escapeHtml(item.key) + '">' +
        '<span class="su-cart__tubline-ic">' +
          '<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.7" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4.5 9.5h15M5.2 9.5l1 10a1.6 1.6 0 0 0 1.6 1.4h8.4a1.6 1.6 0 0 0 1.6-1.4l1-10"/><path d="M6 9.5V8.2A1.7 1.7 0 0 1 7.7 6.5h8.6A1.7 1.7 0 0 1 18 8.2v1.3"/></svg>' +
        '</span>' +
        '<span class="su-cart__tubline-tx" aria-live="polite">Meal Tubs <b data-su-tubline-q>\u00D7 ' + q + '</b></span>' +
        '<b class="su-cart__tubline-price" data-su-tubline-p>' + money + '</b>' +
        '<button type="button" class="su-cart__remove" data-su-remove>Remove</button>' +
      '</article>';
  };

  SUCartDrawer.prototype.renderTubStrip = function (cart) {
    var self = this;
    if (!cart) return;
    if (!self.footEl) self.footEl = self.root.querySelector('.su-cart__foot');
    if (!self.footEl) return;
    if (!self.tubEl) {
      self.tubEl = document.createElement('div');
      self.tubEl.setAttribute('data-su-tub', '');
      /* one delegated listener for the strip's whole life: taps read the
         CURRENT shown count from the instance, never a stale closure */
      self.tubEl.addEventListener('click', function (event) {
        if (event.target.closest('[data-su-tub-plus]')) self.setTubQty(self.tubShown + 1);
        else if (event.target.closest('[data-su-tub-minus]')) self.setTubQty(self.tubShown - 1);
      });
    }
    /* pinned at the top of the footer, above note, subtotal and BUY NOW:
       always on screen the moment the drawer opens, no scrolling needed */
    if (self.footEl.firstElementChild !== self.tubEl) {
      self.footEl.insertBefore(self.tubEl, self.footEl.firstChild);
    }

    var pouches = self.tubPouchUnits(cart);
    var line = self.tubLine(cart);
    var truth = line ? Number(line.quantity || 0) : 0;
    /* optimistic display: while taps are being reconciled, show the
       desired count instantly; truth catches up in the background */
    var shown = (self.tubDesired != null) ? self.tubDesired : truth;
    self.tubShown = shown;

    /* visible whenever there are pouches to offer for OR tubs already in
       the cart. Tubs can never sit invisibly in the subtotal. */
    if (!pouches && !shown) { self.tubEl.innerHTML = ''; return; }
    if (!self.tubInfo) { self.tubEl.innerHTML = ''; self.loadTubInfo(); return; }

    var price = self.tubInfo.price;

    /* one form, always: the same slim line at zero and at forty.
       The whisper under it scopes the offer to pouches, so tub-meal
       buyers never wonder about the tub they already get. */
    var head = 'Meal Tubs \u00B7 \u20B9' + price + ' each';
    var sub = 'For pouch meals';

    var card = self.tubEl.firstElementChild;
    if (card) {
      /* patch the texts in place, no DOM churn */
      card.querySelector('.su-cart__tub-tx b').textContent = head;
      var rowQ = self.root.querySelector('[data-su-tubline-q]');
      var rowP = self.root.querySelector('[data-su-tubline-p]');
      if (rowQ) rowQ.textContent = '\u00D7 ' + shown;
      if (rowP) rowP.textContent = self.money(shown * price * 100);
      var countEl = card.querySelector('.su-cart__tub-step b');
      if (countEl.textContent !== String(shown)) {
        countEl.textContent = shown;
        /* transition-based pulse: retargets cleanly under rapid taps */
        countEl.style.transform = 'scale(1.12)';
        requestAnimationFrame(function () {
          requestAnimationFrame(function () { countEl.style.transform = ''; });
        });
      }
      return;
    }

    /* state changed (or first paint): rebuild with the entrance animation
       bridging the layout jump between intro and counter */
    var icon = '<svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.6" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M4.5 9.5h15M5.2 9.5l1 10a1.6 1.6 0 0 0 1.6 1.4h8.4a1.6 1.6 0 0 0 1.6-1.4l1-10"/><path d="M6 9.5V8.2A1.7 1.7 0 0 1 7.7 6.5h8.6A1.7 1.7 0 0 1 18 8.2v1.3"/></svg>';
    self.tubEl.innerHTML =
      '<div class="su-cart__tub">' +
        '<span class="su-cart__tub-ic">' + icon + '</span>' +
        '<div class="su-cart__tub-tx"><b>' + head + '</b><span>' + sub + '</span></div>' +
        '<span class="su-cart__tub-step">' +
          '<button type="button" data-su-tub-minus aria-label="Fewer tubs">\u2212</button>' +
          '<b>' + shown + '</b>' +
          '<button type="button" data-su-tub-plus aria-label="More tubs">+</button>' +
        '</span>' +
      '</div>';
  };

  /* Optimistic, self-reconciling quantity: every tap repaints instantly
     and moves the desired count; one background pump keeps issuing
     ABSOLUTE cart writes until the cart equals the latest desire, so
     rapid taps chain (1,2,3...) instead of being dropped, and a stale
     snapshot can never double-add. On failure: snap back and say so. */
  SUCartDrawer.prototype.setTubQty = function (qty) {
    var self = this;
    if (!self.tubInfo || !self.cart) return;
    self.tubDesired = Math.max(0, qty);
    self.renderTubStrip(self.cart);
    self.pumpTub();
  };

  SUCartDrawer.prototype.pumpTub = function () {
    var self = this;
    if (self.tubPumping) return;
    self.tubPumping = true;

    var step = function () {
      var want = self.tubDesired;
      var line = self.tubLine(self.cart);
      var truth = line ? Number(line.quantity || 0) : 0;

      if (want == null || want === truth) {
        self.tubPumping = false;
        self.tubDesired = null;
        self.renderTubStrip(self.cart);
        return;
      }

      var req;
      if (!line && want > 0) {
        req = self.request('cart/add.js', {
          method: 'POST', headers: JSON_HEADERS,
          body: JSON.stringify({ items: [{ id: self.tubInfo.variantId, quantity: want, properties: { _su_src: self.TUB_TAG } }] })
        });
      } else {
        req = self.request('cart/change.js', {
          method: 'POST', headers: JSON_HEADERS,
          body: JSON.stringify({ id: line.key, quantity: want })
        });
      }

      req.then(function () { return self.refresh(false); })
        .then(function () { step(); })
        .catch(function () {
          self.tubPumping = false;
          self.tubDesired = null;
          self.refresh(false);
          self.toast('Could not update tubs, please try again');
        });
    };
    step();
  };

  SUCartDrawer.prototype.saveNote = function () {
    var self = this;
    if (!self.noteEl) return;
    clearTimeout(self.noteTimer);
    self.noteTimer = setTimeout(function () {
      self.request('cart/update.js', {
        method: 'POST', headers: JSON_HEADERS,
        body: JSON.stringify({ note: self.noteEl.value })
      }).catch(function () {});
    }, 600);
  };

  /* ---------------- drawer open/close ---------------- */

  SUCartDrawer.prototype.open = function () {
    this.markSeen();
    if (this.root.classList.contains('is-open')) return;
    this.scrollY = window.scrollY || 0;
    document.documentElement.classList.add('su-cart-lock');
    document.body.classList.add('su-cart-lock');
    this.root.classList.add('is-open');
    var closeBtn = this.root.querySelector('[data-su-close]');
    if (closeBtn) setTimeout(function () { closeBtn.focus(); }, 60);
  };

  SUCartDrawer.prototype.close = function () {
    clearTimeout(this.flushTimer);
    this.flushQty();
    if (!this.root.classList.contains('is-open')) return;
    this.root.classList.remove('is-open');
    document.documentElement.classList.remove('su-cart-lock');
    document.body.classList.remove('su-cart-lock');
    window.scrollTo(0, this.scrollY);
  };

  /* ---------------- FX ---------------- */

  SUCartDrawer.prototype.toast = function (message) {
    var self = this;
    if (!self.toastEl || !message) return;
    self.toastEl.textContent = message;
    self.toastEl.hidden = false;
    clearTimeout(self.toastTimer);
    self.toastTimer = setTimeout(function () { self.toastEl.hidden = true; }, 2200);
  };

  SUCartDrawer.prototype.burst = function () {
    var el = this.burstEl;
    if (!el) return;
    var colors = ['#C79A1C', '#241C10', '#E8D191', '#2F7A4A', '#8F6C0C'];
    for (var i = 0; i < 16; i++) {
      var s = document.createElement('i');
      s.style.left = (6 + Math.random() * 88) + '%';
      s.style.background = colors[i % colors.length];
      s.style.animationDelay = (Math.random() * 0.18) + 's';
      s.style.transform = 'rotate(' + (Math.random() * 180) + 'deg)';
      el.appendChild(s);
      (function (node) { setTimeout(function () { node.remove(); }, 1300); })(s);
    }
  };

  SUCartDrawer.prototype.setLoading = function (button, on) {
    if (!button) return;
    if (on) {
      button.dataset.suOriginalHtml = button.innerHTML;
      button.textContent = 'Adding…';
      button.disabled = true;
    } else {
      if (button.dataset.suOriginalHtml != null) {
        button.innerHTML = button.dataset.suOriginalHtml;
        delete button.dataset.suOriginalHtml;
      }
      button.disabled = false;
    }
  };

  /* ---------------- strip foreign scarcity/timer widgets ---------------- */

  SUCartDrawer.prototype.stripInjectedNodes = function () {
    var panel = this.panel;
    if (!panel) return;

    var PATTERNS = /checkout within|products are limited|limited stock|reserved for|expires in/i;

    panel.querySelectorAll('div, p, span, section').forEach(function (node) {
      /* never touch our own markup or the checkout button area */
      if (node.className && String(node.className).indexOf('su-cart__') === 0) return;
      if (node.closest('.su-cart__checkout-form')) return;
      if (node.children.length > 2) return;               /* only leaf-ish nodes */

      var text = (node.textContent || '').trim();
      if (text.length > 0 && text.length < 120 && PATTERNS.test(text)) {
        var kill = node;
        /* climb to the injected wrapper, but never past our own elements */
        while (
          kill.parentElement &&
          !String(kill.parentElement.className || '').match(/su-cart__/) &&
          kill.parentElement !== panel
        ) { kill = kill.parentElement; }
        kill.style.setProperty('display', 'none', 'important');
      }
    });
  };

  SUCartDrawer.prototype.watchInjectedNodes = function () {
    var self = this;
    if (!self.panel || self.injectObserver) return;
    self.stripInjectedNodes();
    self.injectObserver = new MutationObserver(function () {
      clearTimeout(self.stripTimer);
      self.stripTimer = setTimeout(function () { self.stripInjectedNodes(); }, 40);
    });
    self.injectObserver.observe(self.panel, { childList: true, subtree: true });
  };

  /* ---------------- events ---------------- */

  SUCartDrawer.prototype.bindDrawerEvents = function () {
    var self = this;

    if (self.barEl) {
      self.barEl.addEventListener('click', function (event) {
        event.preventDefault();
        self.open();
      });
    }

    self.root.addEventListener('click', function (event) {
      if (event.target.closest('[data-su-close]')) { event.preventDefault(); self.close(); return; }

      var upsell = event.target.closest('[data-su-upsell-add]');
      if (upsell) { event.preventDefault(); self.addVariant(upsell.dataset.variantId, upsell); return; }

      var copy = event.target.closest('[data-su-copy]');
      if (copy) { event.preventDefault(); self.copyCode(copy); return; }

      var swapBtn = event.target.closest('[data-su-swap]');
      if (swapBtn) {
        event.preventDefault();
        var swapCard = swapBtn.closest('.su-cart__swap');
        if (swapCard) {
          self.doSwap(swapCard.getAttribute('data-line-key'),
                      swapCard.getAttribute('data-variant-id'),
                      swapCard.getAttribute('data-save'),
                      swapCard.getAttribute('data-pack-price'), swapBtn);
        }
        return;
      }
      var swapX = event.target.closest('[data-su-swap-dismiss]');
      if (swapX) {
        event.preventDefault();
        var dCard = swapX.closest('.su-cart__swap');
        if (dCard) {
          if (!self.swapRejected) self.swapRejected = {};
          self.swapRejected[dCard.getAttribute('data-handle')] = true;   /* by product, survives re-keying */
          dCard.remove();
          if (self.cart) self.renderSwap(self.cart);   /* offer the next fresh product */
        }
        return;
      }

      var line = event.target.closest('[data-su-line]');
      if (!line) return;
      var key = line.dataset.lineKey;
      var input = line.querySelector('[data-su-qty]');
      var qty = Math.max(0, Number(input && input.value || 1));

      if (event.target.closest('[data-su-minus]')) { event.preventDefault(); self.changeLine(key, Math.max(0, qty - 1)); }
      else if (event.target.closest('[data-su-plus]')) { event.preventDefault(); self.changeLine(key, qty + 1); }
      else if (event.target.closest('[data-su-undo]')) { event.preventDefault(); self.undoSwap(key, event.target.closest('[data-su-undo]')); }
      else if (event.target.closest('[data-su-remove]')) { event.preventDefault(); self.changeLine(key, 0); self.toast('Item removed'); }
    });

    self.root.addEventListener('change', function (event) {
      var input = event.target.closest('[data-su-qty]');
      if (!input) return;
      var line = input.closest('[data-su-line]');
      if (!line) return;
      self.changeLine(line.dataset.lineKey, Math.max(0, Math.floor(Number(input.value) || 0)));
    });

    if (self.noteToggle && self.noteEl) {
      self.noteToggle.addEventListener('click', function () {
        var show = self.noteEl.hidden;
        self.noteEl.hidden = !show;
        self.noteToggle.textContent = show ? '✎ Hide order note' : '✎ Add order note';
        if (show) self.noteEl.focus();
      });
      self.noteEl.addEventListener('input', function () { self.saveNote(); });
    }

    document.addEventListener('keydown', function (event) {
      if (event.key === 'Escape') self.close();
    });
  };

  SUCartDrawer.prototype.copyCode = function (button) {
    var self = this;
    var code = button.dataset.code || '';
    if (!code) return;
    var done = function () {
      button.dataset.suOriginal = button.dataset.suOriginal || button.textContent;
      button.textContent = 'Copied ✓';
      button.classList.add('is-copied');
      self.toast(code + ' copied');
      clearTimeout(button._suTimer);
      button._suTimer = setTimeout(function () {
        button.textContent = button.dataset.suOriginal;
        button.classList.remove('is-copied');
      }, 1600);
    };
    var fallback = function () {
      try {
        var t = document.createElement('textarea');
        t.value = code; t.setAttribute('readonly', '');
        t.style.position = 'fixed'; t.style.top = '-999px';
        document.body.appendChild(t); t.select();
        document.execCommand('copy'); t.remove();
        done();
      } catch (err) { self.toast('Code: ' + code); }
    };
    if (navigator.clipboard && window.isSecureContext) {
      navigator.clipboard.writeText(code).then(done).catch(fallback);
    } else { fallback(); }
  };

  SUCartDrawer.prototype.bindGlobalEvents = function () {
    var self = this;
    if (window.SUCartGlobalBound) return;
    window.SUCartGlobalBound = true;

    /* 1) Observe cart mutations made by the theme or other apps (Horizon adds via fetch). */
    if (!window.__suFetchPatched) {
      window.__suFetchPatched = true;
      var originalFetch = window.fetch;
      window.fetch = function () {
        var args = arguments;
        var url = '';
        try { url = String(args[0] && args[0].url ? args[0].url : args[0] || ''); } catch (e) {}
        var promise = originalFetch.apply(this, args);
        var inst = window.SUCartDrawerInstance;
        if (inst && inst.selfRequests === 0 && !inst.giftSyncing && /\/cart\/(add|change|update|clear)(\.js)?([?/]|$)/.test(url)) {
          promise.then(function (response) {
            if (response && response.ok) inst.onExternalMutation(/\/cart\/add/.test(url));
          }).catch(function () {});
        }
        return promise;
      };
    }

    /* 2) Fallback for plain HTML product forms (no theme JS). Runs in bubble phase
          and only when the theme did NOT already handle the submit. */
    document.addEventListener('submit', function (event) {
      var inst = window.SUCartDrawerInstance;
      var form = event.target;
      if (!inst || !form || !form.matches || !form.matches('form[action*="/cart/add"]')) return;
      if (event.defaultPrevented) return;             /* theme JS handled it — fetch patch will catch it */
      if (form.closest('[data-su-cart]')) return;
      event.preventDefault();

      var formData = new FormData(form);
      if (!formData.get('quantity')) formData.set('quantity', '1');
      var button = form.querySelector('[type="submit"], button:not([type])');
      inst.setLoading(button, true);
      inst.request('cart/add.js', { method: 'POST', body: formData })
        .then(function () { return inst.afterAdd('Added to cart'); })
        .catch(function (err) { inst.toast(err.message || 'Could not add product'); })
        .finally(function () { inst.setLoading(button, false); });
    }, false);

    /* 3) Cart icon clicks open our drawer instead of the native one / cart page. */
    var TRIGGERS = [
      '#cart-icon-bubble', '.cart-icon-bubble', '.header__icon--cart',
      'a[href$="/cart"]', 'a[href="/cart"]',
      '[aria-controls="cart-drawer"]', '[aria-controls="CartDrawer"]',
      'button[aria-label*="cart" i]', 'cart-icon'
    ].join(',');

    document.addEventListener('click', function (event) {
      var inst = window.SUCartDrawerInstance;
      if (!inst) return;
      var target = event.target;
      if (!target || target.closest('[data-su-cart]')) return;
      if (target.closest('form[action*="/cart/add"]')) return;

      var trigger = target.closest(TRIGGERS);
      if (!trigger) return;

      /* Never hijack real add-to-cart buttons */
      var label = ((trigger.getAttribute('aria-label') || '') + ' ' + (trigger.className || '')).toLowerCase();
      if (label.indexOf('add') !== -1) return;

      event.preventDefault();
      event.stopPropagation();
      if (typeof event.stopImmediatePropagation === 'function') event.stopImmediatePropagation();
      inst.refresh(true);
    }, true);
  };

  /* ---------------- init ---------------- */

  function init() {
    var root = document.querySelector('[data-su-cart]');
    if (!root || root.dataset.suInitialized === 'true') return;
    root.dataset.suInitialized = 'true';
    new SUCartDrawer(root);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', init);
  document.addEventListener('shopify:section:select', function (event) {
    var inst = window.SUCartDrawerInstance;
    if (inst && event.target && event.target.querySelector && event.target.querySelector('[data-su-cart]')) {
      inst.open();
    }
  });
})();